import React, { memo, useCallback, useMemo } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space } from 'antd';
import classNames from 'classnames';

import MoreVertIcon from '@/assets/icons/more_vert.svg';
import {
  getStartModeConfig,
  getStartModeLabel,
  isBulkOperationsEnabled,
} from './config/processStartMode';
import { defaultProps, propTypes } from './config/propTypes';
import styles from './style.less';
import { arePropsEqual } from './utils/arePropsEqual';
import {
  createBulkDownloadItem,
  createConfigItem,
  createDeleteItem,
  createDetailItem,
  createEditItem,
  createFavoriteItem,
  createTaskTracesItem,
  createProcessConfigItem,
  createStartItem,
  createStartModeItem,
  separatePriorityItems,
} from './utils/itemFactory';

const actionLabel = (item) => {
  if (typeof item?.label === 'string' && item.label) return item.label;
  if (typeof item?.title === 'string' && item.title) return item.title;
  return undefined;
};

const TableRowButtons = (props) => {
  const {
    disabled,
    mode = 'auto',
    isMobileView = false,
    forceDropdown = false,
    userRole,
    formatMessage,
    onStart,
    onFavorite,
    onTaskTraces,
    isFavorite,
    onConfig,
    onProcessConfig,
    onDetail,
    onEdit,
    onRemove,
    all,
    onBulkDownload,
    onStartModeClick,
    startMode: startModeProp,
    isBulkRequestCreationEnabled,
    BulkUploadComponent,
    viewMode,
    configLabel,
    customItems = [],
    actionOrder,
  } = props;

  const handleStart = useCallback((e) => onStart?.(e), [onStart]);
  const handleFavorite = useCallback(() => onFavorite?.(), [onFavorite]);
  const handleTaskTraces = useCallback((e) => onTaskTraces?.(e), [onTaskTraces]);
  const handleConfig = useCallback((e) => onConfig?.(e), [onConfig]);
  const handleProcessConfig = useCallback((e) => onProcessConfig?.(e), [onProcessConfig]);
  const handleDetail = useCallback((e) => onDetail?.(e), [onDetail]);
  const handleEdit = useCallback(() => onEdit?.(), [onEdit]);
  const handleRemove = useCallback(() => onRemove?.(), [onRemove]);
  const handleBulkDownload = useCallback(() => onBulkDownload?.(), [onBulkDownload]);
  const handleStartModeClick = useCallback((e) => onStartModeClick?.(e), [onStartModeClick]);

  const isBulkEnabled = useMemo(
    () => isBulkOperationsEnabled(isBulkRequestCreationEnabled, startModeProp),
    [isBulkRequestCreationEnabled, startModeProp],
  );

  const startModeLabel = useMemo(() => getStartModeLabel(startModeProp, formatMessage), [
    formatMessage,
    startModeProp,
  ]);

  const items = useMemo(() => {
    const menuItems = [];

    // Conditional insertion logic preserved - order determined by actionOrder prop
    if (!disabled && onStart && viewMode !== 'grid') menuItems.push(createStartItem(handleStart));
    if (!disabled && startModeProp && onStartModeClick && viewMode !== 'grid') {
      const startModeItem = createStartModeItem(
        startModeProp,
        handleStartModeClick,
        startModeLabel,
      );
      if (startModeItem) menuItems.push(startModeItem);
    }

    if (!disabled && onFavorite && viewMode !== 'grid') {
      menuItems.push(createFavoriteItem(handleFavorite, isFavorite));
    }

    if (!disabled && onTaskTraces && viewMode !== 'grid') {
      menuItems.push(createTaskTracesItem(handleTaskTraces));
    }

    if (userRole?.canManage && onProcessConfig) {
      menuItems.push(createProcessConfigItem(handleProcessConfig, disabled));
    }

    if (onConfig) menuItems.push(createConfigItem(handleConfig, disabled, configLabel));

    if (customItems && customItems.length > 0) {
      menuItems.push(...customItems);
    }

    if (!userRole?.isUserRole && onDetail) menuItems.push(createDetailItem(handleDetail));

    if (!disabled && !userRole?.isUserRole && onEdit) {
      menuItems.push(createEditItem(handleEdit, disabled));
    }

    if (!disabled && isBulkEnabled && BulkUploadComponent) {
      menuItems.push({
        key: 'bulkUpload',
        label: BulkUploadComponent,
      });
    }

    if (!disabled && isBulkEnabled && onBulkDownload) {
      menuItems.push(createBulkDownloadItem(handleBulkDownload));
    }

    if (!disabled && !userRole?.isUserRole && (onRemove || all)) {
      menuItems.push(createDeleteItem(handleRemove, disabled));
    }

    return menuItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabled,
    onStartModeClick,
    startModeProp,
    onStart,
    onFavorite,
    onTaskTraces,
    isFavorite,
    onConfig,
    onEdit,
    onRemove,
    onBulkDownload,
    viewMode,
    isBulkEnabled,
    startModeLabel,
    onProcessConfig,
    onDetail,
    all,
    userRole?.isUserRole,
    userRole?.canManage,
    configLabel,
    customItems,
    actionOrder,
    BulkUploadComponent,
    handleTaskTraces,
    handleStart,
    handleFavorite,
  ]);

  const shouldUseDropdown =
    isMobileView || mode === 'dropdown' || forceDropdown || (mode === 'auto' && items.length > 3);

  const gridActionsClass = viewMode === 'grid' ? 'ant-btn-icon' : undefined;
  const actionsSpaceSize = viewMode === 'grid' ? 12 : 24;

  if (shouldUseDropdown) {
    if (items.length === 0) return null;
    if (items.length === 1) {
      const item = items[0];
      const label = actionLabel(item);
      return (
        <Button
          type="link"
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled || disabled}
          title={label}
          aria-label={label}
        />
      );
    }

    const { priorityItems, dropdownItems } = separatePriorityItems(items, actionOrder);

    if (dropdownItems.length === 0) {
      return (
        <Space size={actionsSpaceSize} className="ant-row-actions">
          {priorityItems.map((item) => {
            const label = actionLabel(item);
            return (
              <Button
                key={item.key}
                type="link"
                className={gridActionsClass}
                icon={item.icon}
                onClick={item.onClick}
                disabled={item.disabled || disabled}
                data-disabled={item.dataDisabled}
                data-active={item.dataActive}
                title={label}
                aria-label={label}
              />
            );
          })}
        </Space>
      );
    }

    return (
      <Space size={actionsSpaceSize} className="ant-row-actions">
        {priorityItems.map((item) => {
          const label = actionLabel(item);
          return (
            <Button
              key={item.key}
              type="link"
              className={gridActionsClass}
              icon={item.icon}
              onClick={item.onClick}
              disabled={item.disabled || disabled}
              data-disabled={item.dataDisabled}
              data-active={item.dataActive}
              title={label}
              aria-label={label}
            />
          );
        })}
        <Dropdown
          menu={{ items: dropdownItems }}
          disabled={disabled}
          overlayClassName={styles.menu}
          className={classNames(styles.trigger, { [styles.isGridMode]: viewMode === 'grid' })}
          placement={viewMode === 'grid' ? 'bottomLeft' : 'left'}
          trigger={['hover', 'click']}
        >
          <Button
            type="link"
            className={gridActionsClass}
            icon={<img alt="icon" src={MoreVertIcon} className="anticon" />}
            disabled={disabled}
          />
        </Dropdown>
      </Space>
    );
  }

  // Inline mode - render buttons in Space
  const inlineButtons = [];

  // Start mode button handling
  if (startModeProp && viewMode !== 'grid') {
    const config = getStartModeConfig(startModeProp, startModeLabel, handleStartModeClick);
    if (config) {
      inlineButtons.push(
        <Button
          key="startMode"
          {...config}
          title={startModeLabel || formatMessage?.({ id: 'global.text.start' })}
          aria-label={startModeLabel || formatMessage?.({ id: 'global.text.start' })}
        />,
      );
    }
  }

  // Bulk upload component (passed as render prop) - FIRST
  if (!disabled && isBulkEnabled && BulkUploadComponent) {
    inlineButtons.push(BulkUploadComponent);
  }

  if (!disabled && isBulkEnabled && onBulkDownload) {
    inlineButtons.push(
      <Button
        key="bulkDownload"
        data-active
        title={formatMessage?.({ id: 'global.text.bulkDownload' })}
        className="ant-btn-icon"
        icon={<DownloadOutlined />}
        onClick={handleBulkDownload}
      />,
    );
  }

  // Sort items according to actionOrder before rendering
  const { priorityItems, dropdownItems } = separatePriorityItems(items, actionOrder);
  const sortedItems = [...priorityItems, ...dropdownItems];

  // Regular menu items as inline buttons (now sorted)
  inlineButtons.push(
    ...sortedItems.map((item) => {
      const label = actionLabel(item);
      return (
        <Button
          key={item.key}
          className="ant-btn-icon"
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled}
          data-disabled={item.dataDisabled}
          data-active={item.dataActive}
          title={label}
          aria-label={label}
        />
      );
    }),
  );

  if (inlineButtons.length === 0) return null;

  return (
    <Space size={24} className="ant-row-actions">
      {inlineButtons}
    </Space>
  );
};

TableRowButtons.propTypes = propTypes;
TableRowButtons.defaultProps = defaultProps;

export default memo(TableRowButtons, arePropsEqual);
